
import Vue from "vue";
import * as XLSX from "xlsx";
import Container from "@/components/Container.vue";
import TableCustom from "@/components/TableCustom.vue";
import LoadingGraph from "@/components/LoadingGraph.vue";
import DownloadIcon from "@/components/icons/DownloadIcon.vue";
import SearchIcon from "@/components/icons/SearchIcon.vue";
import BatteryByCompanyPanel from "@/features/operationMonitor/components/BatteryByCompanyPanel.vue";
import { batteryStatusLabel, batteryBadgeStyle, BATTERY_FILTER_OPTIONS } from "@/common/constants/batteryStatus";

const STATUS_OPTIONS = [
  { label: "All Status", value: null },
  { label: "Normal", value: "normal" },
  { label: "Warning", value: "warning" },
  { label: "Alarm", value: "alarm" },
  { label: "Offline", value: "offline" },
  { label: "No Data", value: "no_data" },
];

export default Vue.extend({
  name: "OperationMonitorPage",
  components: { Container, TableCustom, LoadingGraph, DownloadIcon, SearchIcon, BatteryByCompanyPanel },
  data() {
    const headers = [
      { text: "Company", value: "company" },
      { text: "Initial Code", value: "initial_code" },
      { text: "Plant Name", value: "plant_name" },
      { text: "Equipment", value: "equipment" },
      { text: "Model", value: "model" },
      { text: "Sensor ID", value: "sensorId" },
      { text: "Sensor Name", value: "sensorName" },
      { text: "Status", value: "status", sortable: false, textAlign: "center" },
      { text: "Battery", value: "batteryStatus", sortable: false, textAlign: "center" },
      { text: "Last Sync", value: "timestamp", sortable: false },
    ];
    return {
      headers,
      sensors: [] as any[],
      companies: [] as any[],
      selectedCompanyIds: [] as number[],
      lastFetchedCompanyIds: [] as number[],
      searchQuery: "",
      statusFilter: null as string | null,
      statusOptions: STATUS_OPTIONS,
      batteryStatusFilter: null as string | null,
      batteryStatusOptions: BATTERY_FILTER_OPTIONS,
      batteryRows: [] as any[],
      batteryTotals: {} as Record<string, any>,
      isBatteryLoading: false,
      sensorScope: "live" as "live" | "all",
      summary: { total: 0, normal: 0, warning: 0, alarm: 0, offline: 0, no_data: 0 } as Record<string, number>,
      isLoading: false,
      isExporting: false,
      isFetching: false,
      debounce: 0 as any,
      currentPage: 1,
      pageSize: 10,
      totalPages: 0,
      totalItems: 0,
      pageSizeOptions: [
        { label: "5", value: 5 },
        { label: "10", value: 10 },
        { label: "20", value: 20 },
        { label: "50", value: 50 },
      ],
      autoRefresh: true,
      refreshInterval: 600,
      refreshOptions: [
        { label: "1m", value: 60 },
        { label: "5m", value: 300 },
        { label: "10m", value: 600 },
        { label: "15m", value: 900 },
      ],
      refreshTimer: null as any,
      lastUpdated: "" as string,
    };
  },
  computed: {
    summaryCards(): any[] {
      return [
        { label: "Total", key: "total", value: null, textClass: "text-black" },
        { label: "Normal ✅", key: "normal", value: "normal", textClass: "color-status-normal" },
        { label: "Warning ⚠️", key: "warning", value: "warning", textClass: "color-status-warning" },
        { label: "Alarm 🚨", key: "alarm", value: "alarm", textClass: "color-status-alarm" },
        { label: "Offline 💤", key: "offline", value: "offline", textClass: "color-status-offline" },
        { label: "No Data ⏳", key: "no_data", value: "no_data", textClass: "color-status-disabled" },
      ];
    },
    activeCompanyId(): number | null {
      // Highlights the corresponding row in the rollup panel when the company
      // dropdown (manually, or via a rollup click) narrows to exactly one company.
      return this.selectedCompanyIds.length === 1 ? this.selectedCompanyIds[0] : null;
    },
    activeFilters(): { key: "search" | "company" | "status" | "battery"; text: string }[] {
      const chips: { key: "search" | "company" | "status" | "battery"; text: string }[] = [];
      if (this.searchQuery) chips.push({ key: "search", text: `Search: "${this.searchQuery}"` });
      if (this.selectedCompanyIds.length === 1) {
        const found = this.companies.find((c: any) => c.id === this.selectedCompanyIds[0]);
        chips.push({ key: "company", text: `Company: ${found ? found.title : "1 selected"}` });
      } else if (this.selectedCompanyIds.length > 1) {
        chips.push({ key: "company", text: `Company: ${this.selectedCompanyIds.length} selected` });
      }
      if (this.statusFilter) {
        const found = this.statusOptions.find((o: any) => o.value === this.statusFilter);
        chips.push({ key: "status", text: `Status: ${found ? found.label : this.statusFilter}` });
      }
      if (this.batteryStatusFilter) {
        const found = this.batteryStatusOptions.find((o: any) => o.value === this.batteryStatusFilter);
        chips.push({ key: "battery", text: `Battery: ${found ? found.label : this.batteryStatusFilter}` });
      }
      return chips;
    },
  },
  async created() {
    const role = this.$store.getters.user?.role?.toUpperCase();
    if (role !== "ADMIN") {
      this.$router.replace({ name: "CompaniesPage" });
      return;
    }
    await this.fetchCompanies();
    await Promise.all([this.fetchData(), this.fetchSummary(), this.fetchBatteryByCompany()]);
  },
  mounted() {
    this.startTimer();
  },
  beforeDestroy() {
    this.stopTimer();
    clearTimeout(this.debounce);
  },
  methods: {
    currentParams(extra: Record<string, any> = {}) {
      return {
        search: this.searchQuery || undefined,
        company_ids: this.selectedCompanyIds.length ? this.selectedCompanyIds : undefined,
        status: this.statusFilter || undefined,
        battery_status: this.batteryStatusFilter || undefined,
        scope: this.sensorScope,
        ...extra,
      };
    },
    async fetchData(silent = false) {
      try {
        if (!silent) this.isLoading = true;
        this.isFetching = true;
        const response = await (this as any).$dep.operationMonitorService.getRvsSensors(
          this.currentParams({ page: this.currentPage, size: this.pageSize })
        );
        this.sensors = response.items || [];
        this.totalItems = response.total || 0;
        this.totalPages = this.totalItems > 0 ? Math.ceil(this.totalItems / this.pageSize) : 0;
        this.lastFetchedCompanyIds = [...this.selectedCompanyIds];
        this.lastUpdated = (this as any).$date(new Date()).format("HH:mm:ss");
      } catch {
        this.sensors = [];
        this.totalItems = 0;
        this.totalPages = 0;
      } finally {
        if (!silent) this.isLoading = false;
        this.isFetching = false;
      }
    },
    async fetchSummary() {
      try {
        // Cards show the full machine-health distribution for the current search +
        // company scope, so the `status` filter is intentionally omitted here.
        // `battery_status` IS included, so the cards narrow along with a battery drill.
        const params = {
          search: this.searchQuery || undefined,
          company_ids: this.selectedCompanyIds.length ? this.selectedCompanyIds : undefined,
          battery_status: this.batteryStatusFilter || undefined,
          scope: this.sensorScope,
        };
        const response = await (this as any).$dep.operationMonitorService.getRvsSensorSummary(params);
        this.summary = response || this.summary;
      } catch {
        /* keep previous counts */
      }
    },
    async fetchBatteryByCompany() {
      try {
        this.isBatteryLoading = true;
        // The rollup narrows to match the company dropdown, same as any manual
        // selection -- there is no separate drill-down state anymore.
        const params = {
          search: this.searchQuery || undefined,
          company_ids: this.selectedCompanyIds.length ? this.selectedCompanyIds : undefined,
          status: this.statusFilter || undefined,
          scope: this.sensorScope,
        };
        const res = await (this as any).$dep.operationMonitorService.getRvsBatteryByCompany(params);
        this.batteryRows = res?.rows || [];
        this.batteryTotals = res?.totals || {};
      } catch {
        this.batteryRows = [];
        this.batteryTotals = {};
      } finally {
        this.isBatteryLoading = false;
      }
    },
    async fetchCompanies() {
      try {
        const result = await (this as any).$dep.companyUseCase.getCompanies();
        this.companies = result || [];
      } catch {
        this.companies = [];
      }
    },
    refreshAll() {
      this.fetchData();
      this.fetchSummary();
      this.fetchBatteryByCompany();
    },
    onPageChange() {
      this.fetchData(true);
    },
    onPageSizeChange() {
      this.currentPage = 1;
      this.refreshAll();
    },
    onStatusFilterChange() {
      this.currentPage = 1;
      this.refreshAll();
    },
    onBatteryStatusFilterChange() {
      this.currentPage = 1;
      this.refreshAll();
    },
    onScopeChange(value: "live" | "all") {
      if (!value || value === this.sensorScope) return;
      this.sensorScope = value;
      this.currentPage = 1;
      this.refreshAll();
    },
    onCompanyFilterChange() {
      const prev = [...this.lastFetchedCompanyIds].sort().join(",");
      const curr = [...this.selectedCompanyIds].sort().join(",");
      if (prev === curr) return;
      this.currentPage = 1;
      this.refreshAll();
    },
    onCardClick(value: string | null) {
      this.statusFilter = value;
      this.currentPage = 1;
      this.refreshAll();
    },
    onBatteryCellSelect({ companyId, statusKey }: { companyId: number; companyName: string; statusKey: string }) {
      // Writes the same state the dropdowns are bound to -- no separate
      // drill-down variable, so the dropdowns, chips, table, health cards and
      // the rollup panel itself all reflect (and narrow to) this selection.
      this.selectedCompanyIds = companyId ? [companyId] : [];
      this.batteryStatusFilter = statusKey;
      this.currentPage = 1;
      this.refreshAll();
    },
    clearFilter(key: "search" | "company" | "status" | "battery") {
      if (key === "search") {
        clearTimeout(this.debounce);
        this.searchQuery = "";
      } else if (key === "company") {
        this.selectedCompanyIds = [];
        this.lastFetchedCompanyIds = [];
      } else if (key === "status") {
        this.statusFilter = null;
      } else if (key === "battery") {
        this.batteryStatusFilter = null;
      }
      this.currentPage = 1;
      this.refreshAll();
    },
    goToModel(modelId: number) {
      if (!modelId) return;
      const href = this.$router.resolve({ name: "ViewModelPage", params: { id: String(modelId) } }).href;
      window.open(href, "_blank");
    },
    statusOf(status: number | null | undefined) {
      if (status === null || status === undefined) return { label: "No Data", bgClass: "bg-status-disabled" };
      if (status === -1) return { label: "Offline", bgClass: "bg-status-offline" };
      if (status === 0) return { label: "Normal", bgClass: "bg-status-normal" };
      if (status === 1) return { label: "Warning", bgClass: "bg-status-warning" };
      if (status === 2) return { label: "Alarm", bgClass: "bg-status-alarm" };
      return { label: "Disabled", bgClass: "bg-status-disabled" };
    },
    formatBattery(battery: number | null | undefined) {
      if (battery === null || battery === undefined) return "-";
      return `${Number(battery).toFixed(2)} V`;
    },
    batteryStatusLabel,
    batteryBadgeStyle,
    formatTimestamp(timestamp: string | null | undefined) {
      if (!timestamp) return "-";
      return (this as any).$date(timestamp).format("DD/MM/YYYY HH:mm");
    },
    startTimer() {
      this.stopTimer();
      if (!this.autoRefresh) return;
      this.refreshTimer = setInterval(() => {
        if (this.isFetching) return;
        this.fetchData(true);
        this.fetchSummary();
      }, this.refreshInterval * 1000);
    },
    stopTimer() {
      if (this.refreshTimer) {
        clearInterval(this.refreshTimer);
        this.refreshTimer = null;
      }
    },
    async exportToExcel() {
      try {
        this.isExporting = true;
        const rows = await (this as any).$dep.operationMonitorService.getRvsSensorsExport(this.currentParams());
        const data = (rows || []).map((r: any, index: number) => ({
          "No.": index + 1,
          Company: r.company || "",
          "Initial Code": r.initial_code || "",
          "Plant Name": r.plant_name || "",
          Equipment: r.equipment || "",
          Model: r.model || "",
          "Sensor ID": r.sensorId || "",
          "Sensor Name": r.sensorName || "",
          Status: this.statusOf(r.overAllSensorStatus).label,
          "Battery Status": batteryStatusLabel(r.batteryStatus),
          "Battery (V)": r.battery === null || r.battery === undefined ? "" : Number(r.battery).toFixed(2),
          "Last Sync": r.timestamp ? (this as any).$date(r.timestamp).format("DD/MM/YYYY HH:mm") : "",
        }));
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(data), "RVS Sensors");

        const batteryByCompany = (this.batteryRows as any[]).map((r) => ({
          Company: r.company || "",
          "Initial Code": r.initial_code || "",
          Critical: r.critical,
          Alarm: r.alarm,
          Warning: r.warning,
          Normal: r.normal,
          Offline: r.offline,
          "No Data": r.no_data,
          Total: r.total,
        }));
        XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(batteryByCompany), "Battery by Company");

        XLSX.writeFile(workbook, `OperationMonitor_${new Date().toISOString().slice(0, 10)}.xlsx`);
      } catch (error: any) {
        this.$store.dispatch("openAlertMessage", {
          open: true,
          message: error?.response?.data?.detail || "error: export failed",
          type: "error",
        });
      } finally {
        this.isExporting = false;
      }
    },
  },
  watch: {
    searchQuery() {
      clearTimeout(this.debounce);
      this.debounce = setTimeout(() => {
        this.currentPage = 1;
        this.refreshAll();
      }, 600);
    },
    autoRefresh() {
      this.startTimer();
    },
    refreshInterval() {
      this.startTimer();
    },
  },
});
